import {List, Record} from 'immutable';
import {detectService} from "../service/cody-wizard/node/detect-service";
import {names} from "../service/cody-wizard/node/names";
import {Graph, MxGraphElement, Node, NodeId, NodeType} from "./Graph";

interface ParsedFilter {
    nodeIds: string[];
    nodeTypes: string[];
    nodeLabels: string[];
    nodeTags: string[];
    notNodeTags: string[];
    strict: boolean;
    service?: string;
}

export interface TreeProps {
    elements: List<Node>;
    filter: string;
    focus: boolean;
}

export const defaultTreeProps: TreeProps = {
    elements: List(),
    filter: '',
    focus: false,
};

export const createTreeFromGraph = (graph: Graph): ElementsTree => {
    return createTreeFromElements(graph.getRoot().children());
};

export const createTreeFromElements = (elements: List<Node>): ElementsTree => {
    return new ElementsTree({elements: elements.filter(c => isValidNode(c))});
}

export const parseFilter = (filter: string): ParsedFilter => {
    const parts = filter.split(';');

    const nodeIds: string[] = [];
    const nodeTypes: string[] = [];
    const nodeLabels: string[] = [];
    const nodeTags: string[] = [];
    const notNodeTags: string[] = [];

    parts.forEach(part => {
        if(part.search('id:') !== -1) {
            nodeIds.push(part.replace('id:', '').trim());
        }else if(part.search('type:') !== -1) {
            nodeTypes.push(part.replace('type:', '').trim().toLowerCase());
        } else if(part.search('!tag:') !== -1) {
            notNodeTags.push(part.replace('!tag:', '').trim().toLowerCase());
        } else if(part.trim().search(/^!#/) !== -1) {
            notNodeTags.push(part.replace('!#', '').trim().toLowerCase());
        } else if(part.search('tag:') !== -1) {
            nodeTags.push(part.replace('tag:', '').trim().toLowerCase());
        } else if(part.trim().search(/^#/) !== -1) {
            nodeTags.push(part.replace('#', '').trim().toLowerCase());
        } else {
            nodeLabels.push(part.trim());
        }
    })

    return {nodeIds, nodeTypes, nodeLabels, nodeTags, notNodeTags, strict: false};
}

export const isFeatureTypeFilter = (filter: ParsedFilter): boolean => {
    return filter.nodeTypes.length === 1 && filter.nodeTypes[0] === NodeType.feature;
}

export const isContextTypeFilter = (filter: ParsedFilter): boolean => {
    return filter.nodeTypes.length === 1 && filter.nodeTypes[0] === NodeType.boundedContext.toLowerCase();
}

const isValidNode = (node: Node): boolean => {
    return !node.getTags().contains(ispConst.TAG_ELEMENTS_TREE_HIDDEN)
}

const matchNode = (node: Node, filter: ParsedFilter): boolean => {
    // Layer always match
    if(node.isLayer()) {
        return true;
    }

    if(!isValidNode(node)) {
        return false;
    }

    if(filter.nodeTypes.length && !filter.nodeTypes.includes(node.getType().toLowerCase())) {
        return false;
    }

    if(filter.nodeIds.length && !filter.nodeIds.includes(node.getId())) {
        return false;
    }

    if(filter.service) {
        const nodeService = detectService(node);
        if(nodeService !== filter.service) {
            return false;
        }
    }

    if(filter.nodeTags.length) {
        for (const tag of filter.nodeTags) {
            if(!node.getTags().contains(tag)) {
                // @TODO: Remove fallback after a while, fixed spelling 2019-11-26
                if(tag === 'planned') {
                    if(!node.getTags().contains('planed')) {
                        return false;
                    }
                } else {
                    return false;
                }
            }
        }
    }

    if(filter.notNodeTags.length) {
        for (const tag of filter.notNodeTags) {
            if(node.getTags().contains(tag)) {
                return false;
            }
        }
    }

    let matchesAllLabelParts = true;

    filter.nodeLabels.forEach(labelFilter => {
        if(filter.strict) {
            if(node.getName() !== labelFilter) {
                matchesAllLabelParts = false;
            }
        } else {
            if(node.getName().toLowerCase().search(labelFilter.toLowerCase()) === -1) {
                matchesAllLabelParts = false;
            }
        }
    });

    return matchesAllLabelParts;
};

const filterChildren = (children: List<Node>, filter: ParsedFilter): List<Node> => {
    const filteredChildren: Node[] = [];
    const isFeatureFilter = isFeatureTypeFilter(filter);
    const isContextFilter = isContextTypeFilter(filter);

    children.forEach(child => {
        if(isFeatureFilter && child.getType() === NodeType.feature) {
            if(matchNode(child, filter)) {
                filteredChildren.push(child.setChildren(child.children().filter(c => isValidNode(c))));
            }

            return;
        }

        if(isContextFilter && child.getType() === NodeType.boundedContext) {
            if(matchNode(child, filter)) {
                filteredChildren.push(child.setChildren(child.children().filter(c => isValidNode(c))));
            }
            return;
        }

        const filteredSubChildren = filterChildren(child.children(), filter);

        if(filteredSubChildren.count() > 0) {
            filteredChildren.push(child.setChildren(filteredSubChildren));
            return;
        }

        if(matchNode(child, filter)) {
            filteredChildren.push(child.setChildren(filteredSubChildren))
        }
    });

    return List(filteredChildren);
};

const filterElements = (elements: List<Node>, filter: ParsedFilter): List<Node> => {
    const filteredElements: Node[] = [];

    elements.forEach(ele => {

       const filteredSubChildren = filterElements(ele.children(), filter);

       if(filteredSubChildren.count() > 0) {
           filteredElements.push(...filteredSubChildren.toArray());
       }

       if(matchNode(ele, filter)) {
           filteredElements.push(ele);
       }
    });

    return List(filteredElements);
}

export class ElementsTree extends Record(defaultTreeProps) implements TreeProps {

    private cache: {[filter: string]: any} = {};

    public mergeTree(other: ElementsTree): ElementsTree {
        // @TODO Implement diffing strategy to only add/remove/update changed elements
        return other.set('filter', this.filter);
    }

    public refresh (): ElementsTree {
        return new ElementsTree({
            elements: this.elements,
            filter: this.filter,
            focus: this.focus
        })
    }

    public getFilteredElementsAsTree(): List<Node> {
        const parsedFilter = parseFilter(this.filter);
        const cacheKey = JSON.stringify(parsedFilter);

        this.initCache();

        if(!this.cache[cacheKey]) {
            this.cache[cacheKey] = filterChildren(this.elements, parsedFilter);
        }

        return this.cache[cacheKey];
    }

    public isFiltered(): boolean {
        return this.filter !== '';
    }

    public getSimilarElements(node: Node): List<Node> {
        const cacheKey = `getSimilarElements_${node.getId()}`;

        this.initCache();

        if(!this.cache[cacheKey]) {
            const nodeName = this.getNamespacedName(node);

            if(nodeName === '') {
                return List([]);
            }

            this.cache[cacheKey] = this.getFilteredElementsByTypeAndLabel(node.getType(), node.getName()).filter(se => {
                return se.getId() !== node.getId()
                    && this.getNamespacedName(se) === nodeName
            });
        }

        return this.cache[cacheKey];
    }

    public getFilteredElementsByTag(tag: string): List<Node> {
        return filterElements(this.elements, {
            nodeIds: [],
            nodeLabels: [],
            strict: true,
            nodeTags: [tag.replace('#', '').toLowerCase()],
            notNodeTags: [],
            nodeTypes: [],
        })
    }

    public getFilteredElementsByTypeAndLabel(nodeType: string, label: string, service?: string): List<Node> {
        return filterElements(this.elements, {
            nodeIds: [],
            nodeLabels: [label],
            strict: true,
            nodeTags: [],
            notNodeTags: [],
            nodeTypes: [nodeType.toLowerCase()],
            service
        })
    }

    public getFilteredElementsByType(nodeType: string, uniqueLabels?: boolean): List<Node> {
        const filteredElements = filterElements(this.elements, {
            nodeIds: [],
            nodeLabels: [],
            strict: true,
            nodeTags: [],
            notNodeTags: [],
            nodeTypes: [nodeType.toLowerCase()]
        }).sortBy(ele => ele.getName());

        if(!uniqueLabels) {
            return filteredElements;
        }

        const namesList: string[] = [];

        return filteredElements.filter(ele => {
            if(namesList.includes(ele.getName())) {
                return false;
            }

            namesList.push(ele.getName());

            return true;
        })
    }

    public getFilteredElementsByTypes(nodeTypes: NodeType[], uniqueLabels?: boolean): List<Node> {
        const filteredElements = filterElements(this.elements, {
            nodeIds: [],
            nodeLabels: [],
            strict: true,
            nodeTags: [],
            notNodeTags: [],
            nodeTypes: nodeTypes.map(f => f.toLowerCase())
        }).sortBy(ele => ele.getName());

        if(!uniqueLabels) {
            return filteredElements;
        }

        const namesList: string[] = [];

        return filteredElements.filter(ele => {
            if(namesList.includes(ele.getName())) {
                return false;
            }

            namesList.push(ele.getName());

            return true;
        })
    }

    public getElement(elementId: NodeId): Node | undefined {
        const cacheKey = `getElement_${elementId}`;

        this.initCache();

        if(!this.cache[cacheKey]) {
            const matchedEles = filterElements(this.elements, {
                nodeIds: [elementId],
                nodeLabels: [],
                nodeTags: [],
                notNodeTags: [],
                nodeTypes: [],
                strict: true
            });

            if (matchedEles.size > 0) {
                this.cache[cacheKey] = matchedEles.first();

            }

            return undefined;
        }

        return this.cache[cacheKey];
    }

    private getNamespacedName(node: Node): string {
        if(node.getType() === NodeType.event) {
            let service = detectService(node);
            if(service) {
                service += '.';
            } else {
                service = '';
            }
            // FQCN of event might include aggregate name, but for similar nodes that's not what we want to compare
            return `${service}` + names(node.getName()).className;
        }

        // @TODO: Inject default service from board settings
        return node.getFQCN('');
    }

    private initCache() {
        if(!this.cache) {
            this.cache = {};
        }
    }
}
